import { faArrowRight } from "@fortawesome/pro-regular-svg-icons/faArrowRight";
import { faTimes } from "@fortawesome/pro-regular-svg-icons/faTimes";
import { faUser } from "@fortawesome/pro-regular-svg-icons/faUser";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState, useEffect } from "react";
import { Link, useLoaderData } from "react-router";

import Config from "../Config";
import { useAppContext } from "../contexts/AppContext";
import { useAppStore } from "../stores/AppStore";
// Direct imports to prevent CSS from other components leaking into root bundle
import { AccountContainer } from "./AccountContainer";
import { KlarnaHomepageBanner } from "./KlarnaHomepageBanner";
import { MiniCartContainer } from "./MiniCartContainer";
import { NavigationLinks } from "./NavigationLinks";

import "../assets/scss/components/_header.scss";
import { PeakLabelAnimation } from "./PeakLabelAnimation";
import { SearchBar } from "./SearchBar";
import { Slider } from "./Slider";
import { WebsiteBanner } from "./WebsiteBanner";

export const Header = () => {
	const loaderData = useLoaderData();
	const appData = useAppContext();
	const [mobileNav, setMobileNav] = useState(false);

	const { Logo } = Config;

	const { peakConfigs: storedPeakConfigs } = useAppStore(({ peakConfigs }) => ({ peakConfigs }));
	const peakConfigs = appData?.peakConfigs || storedPeakConfigs;

	const { websiteBanner: storedWebsiteBanner, setWebsiteBanner } = useAppStore(
		({ websiteBanner, setWebsiteBanner }) => ({ websiteBanner, setWebsiteBanner }),
	);

	// Sync loader data to store when we get fresh data from the server
	useEffect(() => {
		if (loaderData?.websiteBanner && loaderData?.fromServer) {
			setWebsiteBanner(loaderData.websiteBanner);
		}
	}, [loaderData, setWebsiteBanner]);

	// Use loader data (from server or client loader), fallback to store
	const websiteBanner = loaderData?.websiteBanner ?? storedWebsiteBanner;

	const showKlarnaBanner = Config.paymentMethodsEnabled?.klarnaEnabled ? true : false;

	const [uspAutoplay, setUspAutoplay] = useState(true);

	return (
		<div className="header-container">
			<div className="usp-section">
				{websiteBanner?.status && websiteBanner?.text ? (
					<WebsiteBanner
						link={websiteBanner.link}
						text={websiteBanner.text}
						buttonText={websiteBanner.button_text}
					/>
				) : (
					<Slider
						slides={[
							...(showKlarnaBanner
								? [
										{
											element: <KlarnaHomepageBanner setUspAutoplay={setUspAutoplay} />,
										},
									]
								: []),
							{
								element: <p>Order by 10pm for next day delivery</p>,
							},
							{
								element: <p>Free delivery, 7 days a week</p>,
							},
							{
								element: (
									<a
										target="_blank"
										rel="noreferrer"
										aria-label="Our Reviews on Reviews.io"
										href="https://www.reviews.co.uk/company-reviews/store/123-flowers-co-uk"
									>
										12,000+ reviews on Reviews.io <FontAwesomeIcon icon={faArrowRight} />
									</a>
								),
							},
						]}
						slide_duration={5}
						prefix="usp-slider"
						slides_per_row={{
							base: 1,
						}}
						autoplay={uspAutoplay}
						fade={true}
					/>
				)}
			</div>
			<div className="top-section container">
				<div className="row align-items-center justify-content-center">
					<div className="burger-toggle col-2">
						<button
							className="hamburger"
							id="mobile-toggle"
							aria-label="Open Menu"
							onClick={() => {
								setMobileNav(!mobileNav);
							}}
						>
							<svg
								className={`hamburger-svg ${mobileNav ? "open" : ""}`}
								viewBox="0 0 100 100"
								width="35"
								height="35"
								version="1.1"
								xmlns="http://www.w3.org/2000/svg"
								x="0px"
							>
								<line className="hamburger-line  hamburger-line--1" x1="20" y1="50" x2="80" y2="50" />
								<line className="hamburger-line  hamburger-line--2" x1="20" y1="50" x2="80" y2="50" />
								<line className="hamburger-line  hamburger-line--3" x1="20" y1="50" x2="80" y2="50" />
							</svg>
						</button>
					</div>
					<div className="logo-container col-8 col-md-3">
						<Link to="/" aria-label="Homepage" title="Homepage" prefetch="intent">
							{/* <Image
								src={logo}
								alt="123 Flowers Logo"
								width="80px"
								imported={true}
							/> */}
							<Logo alt="123 Flowers Logo" width="170px" />
						</Link>

						{peakConfigs?.CHRISTMAS && (
							<PeakLabelAnimation text="Christmas!" link="/christmas-flowers" date={new Date("12-24")} />
						)}

						{peakConfigs?.VALENTINES && (
							<PeakLabelAnimation
								text="Valentine's Day!"
								link="/valentines-flowers"
								date={new Date("02-14")}
							/>
						)}

						{peakConfigs?.MOTHERS_DAY && 1 == 2 && (
							<PeakLabelAnimation
								text="Mother's Day!"
								link="/mothers-day-flowers"
								date={new Date("03-15")}
							/>
						)}
					</div>
					<SearchBar closeNav={() => setMobileNav(false)} />
					<div className="action-buttons col-2 col-md-3">
						<MiniCartContainer />
						{/* <Link
							style={{
								fontWeight: "bold",
								textAlign: "center",
								fontSize: "20px"
							}}
							to="/wishlist"
							aria-label="Wishlist"
							title="Wishlist"
						>
							<FontAwesomeIcon icon={faHeart} />
							<span
								style={{
									display: "block",
									fontSize: "13px"
								}}
							>
								Wishlist
							</span>
						</Link> */}
						<Link
							style={{
								fontWeight: "bold",
								textAlign: "center",
								fontSize: "20px",
							}}
							to="/account"
							aria-label="Account"
							title="Account"
						>
							<FontAwesomeIcon icon={faUser} />
							<span
								style={{
									display: "block",
									fontSize: "13px",
								}}
							>
								Account
							</span>
						</Link>
					</div>
				</div>
			</div>

			<div className={`navigation-bar ${mobileNav ? "open" : ""}`}>
				<div className="container">
					<AccountContainer setMobileNav={() => setMobileNav(false)} />
					<SearchBar mobile={true} instantSearch={false} closeNav={() => setMobileNav(false)} />
					<div className="row align-items-center justify-content-start">
						<NavigationLinks closeNav={() => setMobileNav(false)} />
					</div>
				</div>

				<button
					className="close-button"
					onClick={() => {
						setMobileNav(false);
					}}
				>
					<FontAwesomeIcon icon={faTimes} /> Close Menu
				</button>
			</div>
		</div>
	);
};
